<script setup>
// The landing signature: the money path drawn as a civic instrument. The owners (a
// "pand") on the left, the organisation's OWN account on the right, and a 2px ink
// rail between them. Pandig sits deliberately OFF that line — a chip whose dashed
// stub never reaches the rail, captioned "via Pandig · € 0,00" — so the promise
// "Pandig raakt het geld niet aan" is structurally true, not a slogan.
//
// On mount (once, never looping): the whole card drops in (the same beat as
// .auth-enter elsewhere), then the via-chip drops in, the rail draws itself
// left-to-right, and a cobalt pulse rides it in hard mechanical steps (no blur, no
// shadow on the moving dot — deliberately un-smooth, matching the hard-shadow
// system). The card owns this whole sequence so the parent's hero timeline only has
// to reveal it as a single unit. Reduced-motion users get the final static state
// straight away — the card is simply visible and the pulse dot never appears at all.
//
// What deliberately does NOT animate is the money. This figure used to tick up from
// € 0,00, so for about a second the card read "€ 4.750,00 verwacht / € 1.727,27
// binnen" — indistinguishable from a real collection shortfall on the one instrument
// whose whole job is to be trusted. Both amounts are final from the first readable
// frame; what travels is the rail, the pulse, and a word ("onderweg" → "binnen")
// that can't be mistaken for an amount.
import { ref, computed, onMounted, onUnmounted } from "vue";
import { Landmark } from "@lucide/vue";
import BrandMark from "@/components/BrandMark.vue";
import { formatMoney } from "@/lib/format";
import { gsap, useMotion, reducedMotion, collapseRail, EASE } from "@/lib/motion";

const props = defineProps({
  topLabel: { type: String, required: true },
  ownersLabel: { type: String, required: true },
  amountCents: { type: Number, required: true },
  toLabel: { type: String, required: true },
  ibanLabel: { type: String, required: true },
  landedSuffix: { type: String, default: "binnen" },
  pendingSuffix: { type: String, default: "onderweg" },
  viaLabel: { type: String, default: "via Pandig" },
  viaValue: { type: String, default: "€ 0,00" },
  untouchedLabel: { type: String, required: true },
  currency: { type: String, default: "EUR" },
  startDelay: { type: Number, default: 0.15 },
});

const amountLabel = computed(() => formatMoney(props.amountCents, props.currency));

// The only thing the pulse's arrival changes: which word sits behind the amount.
const arrived = ref(reducedMotion);

const root = ref(null);
const viaChip = ref(null);
const railLine = ref(null);
const railArrow = ref(null);
const pulseDot = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    const railTip = collapseRail(railLine.value);
    gsap.set(root.value, { opacity: 0, x: -5, y: -14 });
    gsap.set(viaChip.value, { opacity: 0, y: -6 });
    gsap.set(railArrow.value, { opacity: 0 });

    // A "travel" label marks the moment the rail finishes drawing — the pulse's
    // fade-in/out and its trip along the rail both hang off it explicitly, rather
    // than GSAP's "<"/">" shortcuts (which only ever refer to the single
    // most-recently-added tween and would silently mis-sequence parallel pieces).
    gsap
      .timeline({ delay: props.startDelay })
      .to(root.value, { opacity: 1, x: 0, y: 0, duration: 0.42, ease: EASE })
      .to(viaChip.value, { opacity: 1, y: 0, duration: 0.3, ease: EASE }, "-=0.12")
      .to(railLine.value, { attr: { x2: railTip }, duration: 0.5, ease: "power2.inOut" }, "-=0.05")
      .addLabel("travel")
      .to(railArrow.value, { opacity: 1, duration: 0.15 }, "travel")
      .to(pulseDot.value, { opacity: 1, duration: 0.05 }, "travel")
      .to(pulseDot.value, { left: "90%", duration: 0.85, ease: "steps(14)" }, "travel")
      .to(pulseDot.value, { opacity: 0, duration: 0.1 }, "travel+=0.73")
      .call(() => (arrived.value = true), null, "travel+=0.73");
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <figure ref="root" class="border-2 border-foreground bg-card p-3 text-foreground shadow-[var(--shadow-hard-lg)] sm:p-7">
    <figcaption class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:text-[0.6875rem]">
      {{ topLabel }}
    </figcaption>

    <!-- The instrument, then the readout — deliberately two rows rather than one
         three-column grid of stacked icon/label/amount. The labels and figures are
         far wider than the icons, and in one grid they starved the middle column:
         on a phone the rail collapsed to a stub and the "via Pandig" chip, being
         wider than the column it was centred in, overhung both endpoints. Giving
         the rail the card's full width between two bare icons is what makes the
         chip's room, and the drawn line, real at any size. -->
    <div class="mt-7 grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:mt-10 sm:gap-4">
      <span class="flex size-9 items-center justify-center border-2 border-foreground bg-secondary text-foreground sm:size-11">
        <BrandMark class="size-4 text-primary sm:size-5" />
      </span>

      <!-- The path. Pandig floats above it, never touching.
           The track is exactly as tall as the icon chips either side, so the grid's
           own `items-center` is what puts the rail on their centre line. The chip is
           anchored to that same line and hangs above it, its dashed stub stopping
           half a step short — the gap IS the argument, so it can't be left to depend
           on the track happening to be tall enough to contain the chip. The row's top
           margin is what leaves the chip its room. -->
      <div class="relative h-9 sm:h-11">
        <div ref="viaChip" class="absolute bottom-[calc(50%+0.5rem)] left-1/2 flex -translate-x-1/2 flex-col items-center">
          <span class="whitespace-nowrap border-2 border-foreground bg-card px-2 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {{ viaLabel }} · {{ viaValue }}
          </span>
          <span class="mt-1 h-2.5 border-l-2 border-dashed border-foreground/35 sm:mt-1.5 sm:h-5" aria-hidden="true" />
        </div>

        <div class="absolute inset-x-0 top-1/2 -translate-y-1/2">
          <svg class="h-5 w-full" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
            <line ref="railLine" x1="1" y1="10" x2="92" y2="10" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke" />
            <path ref="railArrow" d="M88 4 L97 10 L88 16" fill="none" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke" />
          </svg>
          <span ref="pulseDot" class="absolute left-[2%] top-1/2 size-2.5 -translate-y-1/2 opacity-0 border border-foreground bg-primary" aria-hidden="true" />
        </div>
      </div>

      <span class="flex size-9 items-center justify-center border-2 border-foreground bg-primary text-primary-foreground sm:size-11">
        <Landmark class="size-4 sm:size-5" />
      </span>
    </div>

    <div class="mt-1.5 flex items-start justify-between gap-3 sm:mt-3">
      <div class="min-w-0">
        <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{{ ownersLabel }}</p>
        <p class="mt-1 font-mono text-base font-semibold tabular-nums sm:text-2xl">{{ amountLabel }}</p>
      </div>
      <div class="min-w-0 text-right">
        <!-- The IBAN keeps its own line when the label wraps — broken mid-number
             ("NL·· ····" / "··21") it stops reading as an account at all. -->
        <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {{ toLabel }} · <span class="whitespace-nowrap">{{ ibanLabel }}</span>
        </p>
        <p class="mt-1 font-mono text-base font-semibold tabular-nums text-primary sm:text-2xl">
          {{ amountLabel }} <span class="text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground sm:text-xs">{{ arrived ? landedSuffix : pendingSuffix }}</span>
        </p>
      </div>
    </div>

    <p class="mt-2.5 border-t-2 border-foreground/10 pt-2 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground sm:mt-6 sm:pt-4 sm:text-[0.6875rem]">
      {{ untouchedLabel }}
    </p>
  </figure>
</template>
